import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchTransactions } from "../../store/dbInvoices/actions";
import { Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner, Alert, Label } from "reactstrap";
import TableContainer from "../../components/Common/TableContainer";
import TransactionModal from "./TransactionModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ar } from 'date-fns/locale';
import { format, parseISO, isWithinInterval, subDays } from 'date-fns';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';
import { db } from "helpers/firebase/firebaseConfig";
import { collection, query, where, getDocs, limit } from "firebase/firestore";

const ResolvedTransferTransactions = () => {
  const transactions = useSelector((state) => state.transactions);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [isERPInvoiceOpen, setIsERPInvoiceOpen] = useState(false);
  const [selectedForERPInvoice, setSelectedForERPInvoice] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [quickFilter, setQuickFilter] = useState('الكل');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dispatch = useDispatch();


  useEffect(() => {
    dispatch(fetchTransactions());
  }, [dispatch]);

  // Format transaction date
  const formatTransactionDate = (date) => {
    if (!date) return 'غير متوفر';

    try {
      let parsedDate;

      if (typeof date === 'object' && 'seconds' in date) {
        parsedDate = new Date(date.seconds * 1000);
      }
      else if (typeof date === 'string') {
        parsedDate = parseISO(date);
      }
      else if (date instanceof Date) {
        parsedDate = date;
      } else {
        return 'تاريخ غير صالح';
      }

      return format(parsedDate, 'yyyy/MM/dd HH:mm', { locale: ar });
    } catch (error) {
      console.error('خطأ في تحويل التاريخ:', error);
      return 'تاريخ غير صالح';
    }
  };

  // Toggle functions
  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const toggleDateRangeModal = () => setIsDateRangeModalOpen(!isDateRangeModalOpen);
  const toggleExportDropdown = () => setExportDropdownOpen(prevState => !prevState);
  const toggleERPInvoice = () => setIsERPInvoiceOpen((s) => !s);

  const openERPInvoiceModal = (transaction) => {
    setSelectedForERPInvoice(transaction);
    setIsERPInvoiceOpen(true);
  };

  // Excel Export Functions
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredTransactions.map(t => ({
        'تاريخ العملية': formatTransactionDate(t.created_at),
        'مقدم الخدمة': t.serviceProviders?.salon_name || 'غير متوفر',
        'المبلغ (ر.س)': t.total,
        'حالة الدفع': t.paid_status === 'paid' ? 'تم الدفع' : 'قيد الانتظار',
        'تاريخ التنفيذ': t.booking_execute_date ? formatTransactionDate(t.booking_execute_date) : 'غير محدد'
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "الحوالات المكتملة");
    XLSX.writeFile(workbook, "الحوالات_المكتملة.xlsx");
  };

  const exportSingleToExcel = (transaction) => {
    const worksheet = XLSX.utils.json_to_sheet([{
      'رقم العملية': transaction.id,
      'تاريخ العملية': formatTransactionDate(transaction.created_at),
      'مقدم الخدمة': transaction.serviceProviders?.salon_name || 'غير متوفر',
      'المبلغ (ر.س)': transaction.total,
      'حالة الدفع': transaction.paid_status === 'paid' ? 'تم الدفع' : 'قيد الانتظار',
      'تاريخ التنفيذ': transaction.booking_execute_date ? formatTransactionDate(transaction.booking_execute_date) : 'غير محدد',
      'ملاحظات': transaction.comment || 'لا يوجد'
    }]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "تفاصيل الحوالة");
    XLSX.writeFile(workbook, `حوالة_${transaction.serviceProviders?.salon_name || transaction.id}.xlsx`);
  };

  // PDF Export Functions
  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    doc.setFontSize(18);
    doc.text("الحوالات المكتملة", 148.5, 20, { align: 'center' });

    if (startDate || endDate) {
      doc.setFontSize(12);
      doc.text(
        `الفترة: ${startDate ? format(startDate, 'yyyy/MM/dd') : '...'} إلى ${endDate ? format(endDate, 'yyyy/MM/dd') : '...'}`,
        148.5,
        30,
        { align: 'center' }
      );
    }

    const headers = [
      ['رقم', 'تاريخ العملية', 'مقدم الخدمة', 'المبلغ (ر.س)', 'حالة الدفع', 'تاريخ التنفيذ']
    ];

    const data = filteredTransactions.map((t, index) => [
      index + 1,
      formatTransactionDate(t.created_at),
      t.serviceProviders?.salon_name || 'غير متوفر',
      t.total,
      t.paid_status === 'paid' ? 'تم الدفع' : 'قيد الانتظار',
      t.booking_execute_date ? formatTransactionDate(t.booking_execute_date) : 'غير محدد'
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 40,
      styles: {
        font: 'helvetica',
        fontSize: 10,
        halign: 'center'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 20 },
        4: { cellWidth: 25 },
        5: { cellWidth: 30 }
      }
    });

    doc.save("الحوالات_المكتملة.pdf");
  };

  const exportSingleToPDF = (transaction) => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("تفاصيل الحوالة", 105, 15, { align: 'center' });

    doc.setFontSize(12);
    let y = 30;

    const details = [
      ['رقم العملية', transaction.id],
      ['تاريخ العملية', formatTransactionDate(transaction.created_at)],
      ['مقدم الخدمة', transaction.serviceProviders?.salon_name || 'غير متوفر'],
      ['المبلغ', `${transaction.total} ر.س`],
      ['حالة الدفع', transaction.paid_status === 'paid' ? 'تم الدفع' : 'قيد الانتظار'],
      ['تاريخ التنفيذ', transaction.booking_execute_date ? formatTransactionDate(transaction.booking_execute_date) : 'غير محدد'],
      ['ملاحظات', transaction.comment || 'لا يوجد']
    ];

    details.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, 20, y);
      y += 10;
    });

    doc.save(`حوالة_${transaction.id}.pdf`);
  };

  // Filter and table functions
  const handleRowClick = (transaction) => {
    setSelectedTransaction(transaction);
    toggleModal();
  };

  const applyQuickFilter = (range) => {
    setQuickFilter(range);
    let start = null;
    let end = null;

    switch (range) {
      case 'اليوم':
        start = new Date();
        start.setHours(0, 0, 0, 0);
        end = new Date();
        break;
      case 'الأسبوع':
        start = subDays(new Date(), 7);
        end = new Date();
        break;
      case 'الشهر':
        start = subDays(new Date(), 30);
        end = new Date();
        break;
      default:
        break;
    }

    setDateRange([start, end]);
  };

  const applyDateFilter = () => {
    toggleDateRangeModal();
  };

  const clearFilters = () => {
    setDateRange([null, null]);
    setQuickFilter('الكل');
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      // Filter for paid transactions - use payment_status not paid_status
      if (transaction.payment_status !== 'paid') return false;
      
      // Exclude refunded invoices from main list (they'll be in separate section)
      if (transaction.refund_status === 'refunded') return false;

      if (startDate && endDate) {
        try {
          let transactionDate;

          if (transaction.created_at?.seconds) {
            transactionDate = new Date(transaction.created_at.seconds * 1000);
          } else if (transaction.created_at) {
            transactionDate = parseISO(transaction.created_at);
          } else {
            return false;
          }

          return isWithinInterval(transactionDate, {
            start: startDate,
            end: endDate
          });
        } catch (error) {
          console.error('خطأ في تصفية التاريخ:', error);
          return false;
        }
      }

      return true;
    });
  }, [transactions, startDate, endDate]);

  // Filter for refunded invoices
  const refundedInvoices = useMemo(() => {
    return transactions.filter((transaction) => {
      if (transaction.refund_status !== 'refunded') return false;

      if (startDate && endDate) {
        try {
          let transactionDate;

          if (transaction.refunded_at?.seconds) {
            transactionDate = new Date(transaction.refunded_at.seconds * 1000);
          } else if (transaction.refunded_at) {
            transactionDate = parseISO(transaction.refunded_at);
          } else if (transaction.created_at?.seconds) {
            transactionDate = new Date(transaction.created_at.seconds * 1000);
          } else if (transaction.created_at) {
            transactionDate = parseISO(transaction.created_at);
          } else {
            return false;
          }

          return isWithinInterval(transactionDate, {
            start: startDate,
            end: endDate
          });
        } catch (error) {
          console.error('خطأ في تصفية التاريخ:', error);
          return false;
        }
      }

      return true;
    });
  }, [transactions, startDate, endDate]);

  const columns = useMemo(
    () => [
      {
        Header: "تاريخ العملية",
        accessor: "created_at",
        Cell: ({ value }) => formatTransactionDate(value),
      },
      {
        Header: "مقدم الخدمة",
        accessor: "serviceProviders.salon_name",
        Cell: ({ value }) => value || 'غير متوفر',
      },
      {
        Header: "حالة الدفع",
        accessor: "payment_status",
        Cell: ({ value }) => <Status value={value} />,
      },
      {
        Header: "الإجراءات",
        accessor: "action",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => handleRowClick(row.original)}
            >
              عرض التفاصيل
            </Button>
            {/* <Button
              color="info"
              size="sm"
              onClick={() => openERPInvoiceModal(row.original)}
            >
              فاتورة ERP
            </Button> */}
            <Button
              color="success"
              size="sm"
              onClick={() => exportSingleToExcel(row.original)}
            >
              Excel
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  // Columns for refunded invoices (with refund indicators)
  const refundedInvoiceColumns = useMemo(
    () => [
      {
        Header: "تاريخ الاسترجاع",
        accessor: "refunded_at",
        Cell: ({ value }) => formatTransactionDate(value),
      },
      {
        Header: "مقدم الخدمة",
        accessor: "serviceProviders.salon_name",
        Cell: ({ value }) => value || 'غير متوفر',
      },
      {
        Header: "المبلغ (ر.س)",
        accessor: "total",
        Cell: ({ value }) => `${value || 0} ر.س`,
      },
      {
        Header: "حالة الاسترجاع",
        accessor: "refund_status",
        Cell: ({ value }) => (
          <Badge color="success" pill>
            <i className="bx bx-check-circle align-middle me-1" />
            تم الاسترجاع
          </Badge>
        ),
      },
      {
        Header: "رقم إشعار الإرجاع",
        accessor: "refund_erp_id",
        Cell: ({ value }) => (
          value ? (
            <Badge color="info" pill>
              {value}
            </Badge>
          ) : (
            <small className="text-muted">--</small>
          )
        ),
      },
      {
        Header: "الإجراءات",
        accessor: "refund_action",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => handleRowClick(row.original)}
            >
              عرض التفاصيل
            </Button>
            <Button
              color="success"
              size="sm"
              onClick={() => exportSingleToExcel(row.original)}
            >
              Excel
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  // Calculate summary stats
  const totalPaidAmount = filteredTransactions.reduce((sum, t) => sum + (Number(t.total) || 0), 0);
  const totalRefundedAmount = refundedInvoices.reduce((sum, t) => sum + (Number(t.total) || 0), 0);
  const totalTransactions = filteredTransactions.length + refundedInvoices.length;

  return (
    <div className="page-content" dir="rtl">
      <Row>
        <Col lg="12">
          {/* Header with Title & Description */}
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <div>
              <h4 className="mb-1">الحوالات المكتملة</h4>
              <small className="text-muted">عرض المعاملات المدفوعة والمسترجعة مع خيارات التصدير والتصفية.</small>
            </div>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <Badge color="success" pill className="px-3 py-2">
                مدفوع: {filteredTransactions.length}
              </Badge>
              <Badge color="info" pill className="px-3 py-2">
                مسترجع: {refundedInvoices.length}
              </Badge>
            </div>
          </div>

          {/* Summary Stats Cards */}
          <Row className="mb-4">
            <Col md="4" className="mb-3 mb-md-0">
              <Card className="border-0 shadow-sm">
                <CardBody className="py-3">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="text-muted mb-1">إجمالي المدفوع</h6>
                      <h4 className="text-success mb-0">{totalPaidAmount.toFixed(2)} ر.س</h4>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success-light rounded-circle">
                        <i className="bx bx-check-circle font-size-24 text-success"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md="4" className="mb-3 mb-md-0">
              <Card className="border-0 shadow-sm">
                <CardBody className="py-3">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="text-muted mb-1">إجمالي المسترجع</h6>
                      <h4 className="text-info mb-0">{totalRefundedAmount.toFixed(2)} ر.س</h4>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info-light rounded-circle">
                        <i className="bx bx-undo font-size-24 text-info"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md="4" className="mb-3 mb-md-0">
              <Card className="border-0 shadow-sm">
                <CardBody className="py-3">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="text-muted mb-1">إجمالي المعاملات</h6>
                      <h4 className="text-primary mb-0">{totalTransactions}</h4>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary-light rounded-circle">
                        <i className="bx bx-wallet font-size-24 text-primary"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card>
            <CardBody>
              {/* Filters & Export Card */}
              <Card className="border-0 bg-light mb-4">
                <CardBody>
                  <Row className="align-items-end">
                    <Col md="6" lg="5" className="mb-3 mb-md-0">
                      <Label className="d-block text-muted small mb-2">التصفية السريعة</Label>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          color={quickFilter === 'الكل' ? 'primary' : 'light'}
                          onClick={() => applyQuickFilter('الكل')}
                        >
                          الكل
                        </Button>
                        <Button
                          size="sm"
                          color={quickFilter === 'اليوم' ? 'primary' : 'light'}
                          onClick={() => applyQuickFilter('اليوم')}
                        >
                          اليوم
                        </Button>
                        <Button
                          size="sm"
                          color={quickFilter === 'الأسبوع' ? 'primary' : 'light'}
                          onClick={() => applyQuickFilter('الأسبوع')}
                        >
                          الأسبوع
                        </Button>
                        <Button
                          size="sm"
                          color={quickFilter === 'الشهر' ? 'primary' : 'light'}
                          onClick={() => applyQuickFilter('الشهر')}
                        >
                          الشهر
                        </Button>
                      </div>
                    </Col>
                    <Col md="6" lg="4" className="mb-3 mb-md-0">
                      <Button
                        size="sm"
                        color="secondary"
                        outline
                        className="w-100"
                        onClick={toggleDateRangeModal}
                      >
                        <i className="bx bx-calendar me-2"></i>
                        نطاق مخصص
                      </Button>
                    </Col>
                    <Col md="6" lg="3">
                      <div className="d-flex gap-2 align-items-center">
                        <Button
                          size="sm"
                          color="success"
                          className="flex-grow-1"
                          onClick={exportToExcel}
                        >
                          <i className="bx bx-download me-2"></i>
                          تصدير
                        </Button>
                        {(startDate || endDate) && (
                          <Button
                            size="sm"
                            color="link"
                            onClick={clearFilters}
                            className="text-danger"
                          >
                            <i className="bx bx-x"></i>
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Active filter indicator */}
                  {(startDate || endDate) && (
                    <div className="mt-3">
                      <Badge color="info" className="me-2">
                        {startDate ? format(startDate, 'yyyy/MM/dd') : '...'}
                        {' → '}
                        {endDate ? format(endDate, 'yyyy/MM/dd') : '...'}
                      </Badge>
                      <small className="text-muted">
                        {filteredTransactions.length} معاملة موجودة
                      </small>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Paid Transactions Section */}
              {filteredTransactions.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <Badge color="success" className="me-2" pill>
                      {filteredTransactions.length}
                    </Badge>
                    <h5 className="card-title mb-0">
                      <i className="bx bx-check-circle align-middle me-2 text-success"></i>
                      المعاملات المدفوعة
                    </h5>
                  </div>
                  <TableContainer
                    columns={columns}
                    data={filteredTransactions}
                    customPageSize={10}
                    className="custom-header-css"
                  />
                </div>
              )}

              {filteredTransactions.length === 0 && (
                <Alert color="info" className="text-center mb-4">
                  <i className="bx bx-info-circle me-2"></i>
                  لا توجد معاملات مدفوعة بالمعايير المحددة
                </Alert>
              )}
              {/* Refunded Invoices Section */}
              {refundedInvoices.length > 0 && (
                <div className="mb-4">
                  <Card className="border-info bg-light">
                    <CardBody>
                      <div className="d-flex align-items-center mb-3">
                        <Badge color="info" className="me-2" pill>
                          <i className="bx bx-check-circle align-middle me-1" />
                          {refundedInvoices.length}
                        </Badge>
                        <h5 className="card-title mb-0">
                          <i className="bx bx-undo align-middle me-2 text-info" />
                          الفواتير المسترجعة (الاسترجاعات المعتمدة)
                        </h5>
                      </div>
                      
                      <TableContainer
                        columns={refundedInvoiceColumns}
                        data={refundedInvoices}
                        customPageSize={5}
                        className="custom-header-css"
                      />
                    </CardBody>
                  </Card>
                </div>
              )}

              {refundedInvoices.length === 0 && filteredTransactions.length === 0 && (
                <Alert color="warning" className="text-center">
                  <i className="bx bx-info-circle me-2"></i>
                  لا توجد معاملات بالمعايير المحددة
                </Alert>
              )}

              <TransactionModal
                isOpen={isModalOpen}
                toggleModal={toggleModal}
                transaction={selectedTransaction}
              />

              <ProviderERPInvoiceModal
                isOpen={isERPInvoiceOpen}
                toggle={toggleERPInvoice}
                transaction={selectedForERPInvoice}
              />

              <Modal isOpen={isDateRangeModalOpen} toggle={toggleDateRangeModal} dir="rtl">
                <ModalHeader toggle={toggleDateRangeModal}>
                  اختر نطاق التاريخ
                </ModalHeader>
                <ModalBody>
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => setDateRange(update)}
                    inline
                    locale={ar}
                    dateFormat="yyyy/MM/dd"
                    isClearable
                  />
                </ModalBody>
                <ModalFooter>
                  <Button color="primary" onClick={applyDateFilter}>
                    تطبيق
                  </Button>
                  <Button color="secondary" onClick={toggleDateRangeModal}>
                    إلغاء
                  </Button>
                </ModalFooter>
              </Modal>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const Status = ({ value }) => {
  const statusMap = {
    paid: { color: 'success', icon: 'bx bx-check-double', text: 'تم الدفع' },
    pending: { color: 'warning', icon: 'bx bx-time-five', text: 'قيد الانتظار' },
    failed: { color: 'danger', icon: 'bx bx-block', text: 'فشل' }
  };

  const status = statusMap[value] || { color: 'secondary', icon: '', text: value };

  return (
    <Badge color={status.color} className="font-size-12">
      <i className={`${status.icon} align-middle me-1`} />
      {status.text}
    </Badge>
  );
};

// New ERP Invoice Modal for Provider
const ProviderERPInvoiceModal = ({ isOpen, toggle, transaction }) => {
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [platformCommission, setPlatformCommission] = useState(0);
  const [loadingCommission, setLoadingCommission] = useState(false);

  const fetchInvoice = async (invoiceId, signal) => {
    try {
      setLoading(true);
      setError(null);
      setInvoice(null);
      setQrImageUrl(null);

      if (!invoiceId) {
        setError("لا يحتوي السجل على رقم فاتورة ERP.");
        return;
      }

      const res = await fetch(
        `https://node-server-eta-coral.vercel.app/invoice/${encodeURIComponent(invoiceId)}`,
        {
          headers: { "Content-Type": "application/json" },
          signal,
        }
      );
      const json = await res.json();

      const inv = json?.data || (Array.isArray(json?.data) ? json.data[0] : null) || json;
      if (!inv || !inv.name) {
        setError("لم يتم العثور على الفاتورة في ERP.");
        return;
      }
      setInvoice(inv);

      if (inv.ksa_einv_qr && !signal?.aborted) {
        try {
          const imgRes = await fetch(
            `https://node-server-eta-coral.vercel.app/erp-image?path=${encodeURIComponent(inv.ksa_einv_qr)}`,
            { signal }
          );
          if (imgRes.ok) {
            const blob = await imgRes.blob();
            if (!signal?.aborted) setQrImageUrl(URL.createObjectURL(blob));
          }
        } catch {
          // ignore QR fetch failure
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(`خطأ أثناء جلب الفاتورة: ${e.message}`);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  // Helper: fetch platform commission rate
  const getPlatformCommissionRate = async () => {
    try {
      const q = query(collection(db, "platform_settings"), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const rate = snap.docs[0].data().commission_rates || 0;
        return Number(rate);
      }
      return 0;
    } catch (err) {
      console.error("Failed to fetch platform commission rate:", err);
      return 0;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    const id = transaction?.provider_erp_id;
    if (id) fetchInvoice(id, controller.signal);

    // fetch platform commission
    (async () => {
      setLoadingCommission(true);
      const rate = await getPlatformCommissionRate();
      setPlatformCommission(rate);
      setLoadingCommission(false);
    })();

    return () => controller.abort();
  }, [isOpen, transaction?.provider_erp_id]);

  useEffect(() => {
    return () => {
      if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
    };
  }, [qrImageUrl]);

  if (!transaction) return null;

  const providerName =
    transaction?.serviceProviders?.salon_name ||
    transaction?.service_provider_name ||
    "غير متوفر";


  // ✅ Safe fallback for service name
  const serviceName =
    transaction?.serviceProviderServices?.name ||
    transaction?.service_name ||
    (Array.isArray(invoice?.items) && (invoice.items[0]?.item_name || invoice.items[0]?.item_code)) ||
    "غير متوفر";

  const commissionRate = platformCommission;
  const gross = Number(invoice?.grand_total || 0);
  const commission = gross * (commissionRate / 100);
  const vat = commission * 0.15;
  const netToProvider = gross - commission - vat;


  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        فاتورة ERP للمزود - {providerName}
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">جاري تحميل الفاتورة...</p>
          </div>
        ) : error ? (
          <Alert color="danger">{error}</Alert>
        ) : invoice ? (
          <>
            <Card className="mb-3 border border-primary">
              <CardBody>
                <h5 className="card-title mb-3 text-primary">فاتورة مزود الخدمة (ERP)</h5>

                <Row>
                  <Col md={6}>
                    <p><strong>رقم الفاتورة:</strong> {invoice.name}</p>
                    <p><strong>المزود:</strong> {providerName}</p>
                    {/* ✅ Use safe serviceName */}
                    <p><strong>الخدمة:</strong> {serviceName}</p>
                    <p><strong>تاريخ الفاتورة:</strong> {invoice.posting_date}</p>
                  </Col>

                  <Col md={6} className="text-end">
                    {qrImageUrl && (
                      <img
                        src={qrImageUrl}
                        alt="ZATCA QR"
                        style={{ width: 120 }}
                      />
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-3 bg-light">
              <CardBody>
                <h5 className="mb-3">الملخص المالي (Settlement)</h5>

                <Row>
                  <Col md={6}>
                    <p>قيمة الطلب (Gross):</p>
                    <p>
                      عمولة المنصة (
                      {loadingCommission ? "..." : `${transaction.constant_commission}%`}
                      ):
                    </p>
                    <p>VAT على العمولة (15%):</p>
                    <hr />
                    <h5>صافي المستحق للمزود:</h5>
                  </Col>

                  <Col md={6} className="text-end">
                    <p>{gross.toFixed(2)} ر.س</p>
                    <p className="text-danger">
                      - {commission.toFixed(2)} ر.س
                    </p>
                    <p className="text-danger">- {vat.toFixed(2)} ر.س</p>
                    <hr />
                    <h5 className="text-success">
                      {netToProvider.toFixed(2)} ر.س
                    </h5>
                  </Col>
                </Row>
              </CardBody>
            </Card>


            {Array.isArray(invoice.items) && invoice.items.length > 0 && (
              <Card className="mb-3">
                <CardBody>
                  <h5 className="card-title mb-3">البنود</h5>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>الصنف</th>
                          <th>الكمية</th>
                          <th className="text-end">السعر</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.items.map((it, idx) => (
                          <tr key={idx}>
                            <td>{it.item_name || it.item_code}</td>
                            <td>{it.qty}</td>
                            <td className="text-end">{it.rate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            )}

            <Card>
              <CardBody>
                <h5 className="card-title mb-3">ملخص</h5>
                <Row>
                  <Col md={6}>
                    <p><strong>الشركة:</strong> {invoice.company || "N/A"}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>الحالة:</strong> <Badge color="success">مكتمل</Badge></p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </>
        ) : (
          <Alert color="info">لا توجد بيانات للعرض.</Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ResolvedTransferTransactions;